import { SUPPORTED_LANGUAGES } from '../../shared/constants/languages.ts';
import { explicitFormatLocale, type CompanyFormatPrefs } from '../../shared/locale-format.ts';

/**
 * Locale detection for `Intl` date/time formatting.
 *
 * Kept separate from i18next's own language detection: i18next decides which
 * *translation strings* to show (English or Spanish), while this decides which
 * *locale* `Intl.DateTimeFormat` should render dates in. The two normally agree,
 * but this one has an extra job — falling back to a locale that renders dates
 * as month/day/year when the browser's own locale is not supported or is
 * unusable, rather than falling back to a fixed language.
 */

/** Renders numeric dates as month/day/year — the fallback the spec calls for. */
const FALLBACK_LOCALE = 'en-US';

/** Parseable by `Intl` and one of the languages this app is translated into. */
function isUsableLocale(candidate: string): boolean {
  try {
    // Throws on a malformed tag; a well-formed but unknown one is normalized
    // instead, so this alone cannot catch "unsupported" — the language check
    // below does that.
    new Intl.DateTimeFormat(candidate);
  } catch {
    return false;
  }

  const baseLanguage = candidate.split('-')[0]?.toLowerCase();
  return Boolean(baseLanguage && (SUPPORTED_LANGUAGES as readonly string[]).includes(baseLanguage));
}

/**
 * The locale to use for `Intl.DateTimeFormat` calls that format something the
 * user sees.
 *
 * Walks the browser's *entire* ordered language preference list
 * (`navigator.languages`, falling back to the single `navigator.language`)
 * and uses the first entry this app has a translation for — the same
 * first-supported-match selection i18next-browser-languagedetector's
 * `navigator` detector feeds into i18next's own language negotiation. That
 * keeps the two in agreement: a browser reporting
 * `navigator.languages = ['fr-FR', 'es-MX']` gets a Spanish UI *and* Spanish
 * dates, rather than a Spanish UI with English-formatted dates because only
 * the unsupported primary preference was inspected.
 *
 * Falls back to `en-US` when nothing in the list is usable, or there is no
 * `navigator` at all (SSR, tests).
 */
/**
 * The signed-in company's format choices. Null until a session is showing a
 * company, and null again after sign-out. A company that has never saved a
 * locale leaves `locale` null so the browser path below is unchanged.
 */
let companyFormat: CompanyFormatPrefs | null = null;

export function setCompanyFormatPrefs(prefs: CompanyFormatPrefs | null): void {
  companyFormat = prefs;
}

export function getCompanyFormatPrefs(): CompanyFormatPrefs | null {
  return companyFormat;
}

export function getDateLocale(): string {
  // An explicit company locale (including en-US, once somebody saved it)
  // decides separators and date order. A company that has never chosen one
  // keeps the browser locale this function has always returned.
  const chosen = explicitFormatLocale(companyFormat?.locale);
  if (chosen) return chosen;
  const browser = browserDateLocale();
  // A language picked in the app (the crew header's switcher) outranks the
  // browser's: a Spanish sentence around an en-US date reads as a mistake.
  // Only the language is overridden; while the two agree, the browser's own
  // region (en-GB, es-MX) still decides the date order.
  const picked = appLanguage?.split('-')[0]?.toLowerCase();
  if (!picked || !(SUPPORTED_LANGUAGES as readonly string[]).includes(picked)) return browser;
  if (browser.split('-')[0]?.toLowerCase() === picked) return browser;
  return picked === 'en' ? FALLBACK_LOCALE : picked;
}

function browserDateLocale(): string {
  if (typeof navigator === 'undefined') return FALLBACK_LOCALE;

  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language].filter(Boolean);

  for (const candidate of candidates) {
    if (candidate && isUsableLocale(candidate)) return candidate;
  }

  return FALLBACK_LOCALE;
}

/** The language i18next is showing, kept in step by `i18n/index.ts`. */
let appLanguage: string | null = null;

/**
 * Records the language the app is showing. i18next calls this on every
 * resolution — the first detection, which agrees with the browser, and any
 * later `changeLanguage()` from a switcher, which may not.
 */
export function setAppLanguage(language: string | null): void {
  appLanguage = language;
}
